/**
 * A mensagem de uma resposta que falhou — a que a pessoa vai ler (set/2026).
 *
 * O download de arquivo (relatório, original, JPEG da página) tentava `JSON.parse`
 * no corpo do erro e, quando ele não era JSON, **relançava o erro do parser**:
 * uma página de erro do nginx virava «Unexpected token '<'» na tela, que não diz
 * nada a quem clicou nem a quem vai investigar. O status HTTP é o que importa —
 * 502 e 504 são do servidor de entrada, 500 é do backend — e é ele que aparece.
 *
 * Fora do `.tsx` e fora do `api.ts` porque `node --test` é o único caminho de
 * teste do projeto, e isto é decisão de texto, que só se confere lendo.
 */

/** Um corpo maior que isto é lixo para a tela; o resto vai para o console. */
const LIMITE = 180;

const EXPLICACAO: Record<number, string> = {
  502: "o servidor de aplicação não respondeu",
  503: "o servidor está indisponível no momento",
  504: "o servidor demorou demais para responder",
  413: "o arquivo é grande demais para esta rota",
  429: "pedidos demais em pouco tempo; tente de novo em instantes",
};

function pareceHtml(corpo: string): boolean {
  return corpo.startsWith("<");
}

/** O `detail` do FastAPI, quando o corpo for o JSON que ele devolve. */
function detalheDoJson(corpo: string): string | null {
  try {
    const body = JSON.parse(corpo) as Record<string, unknown>;
    if (typeof body.detail === "string" && body.detail.trim()) return body.detail.trim();
    // Erro com código: `{code, message}` — o `code` é para a tela decidir, a
    // `message` é para a pessoa ler.
    if (body.detail && typeof body.detail === "object" && !Array.isArray(body.detail)) {
      const message = (body.detail as { message?: unknown }).message;
      if (typeof message === "string" && message.trim()) return message.trim();
    }
    // Erro de validação: uma lista de objetos com `msg`.
    if (Array.isArray(body.detail)) {
      const partes = body.detail
        .map((item) => (item && typeof item === "object" ? String((item as { msg?: unknown }).msg ?? "") : ""))
        .filter(Boolean);
      if (partes.length) return partes.join("; ");
    }
  } catch {
    // Não era JSON — o corpo cru decide adiante. Nunca relançar daqui: o erro
    // do parser não é o erro que aconteceu.
  }
  return null;
}

/**
 * «502: o servidor de aplicação não respondeu», «409: A arte ainda não está
 * pronta para o relatório.», «500 Internal Server Error».
 */
export function mensagemDaFalha(status: number, statusText: string, corpo: string): string {
  const limpo = (corpo || "").trim();
  const detalhe = detalheDoJson(limpo);
  if (detalhe) return `${status}: ${detalhe}`;
  const explicacao = EXPLICACAO[status];
  if (explicacao) return `${status}: ${explicacao}`;
  if (limpo && !pareceHtml(limpo)) {
    const curto = limpo.length > LIMITE ? `${limpo.slice(0, LIMITE)}…` : limpo;
    return `${status}: ${curto}`;
  }
  return statusText ? `${status} ${statusText}` : `Erro ${status}`;
}
